<script setup lang="ts">
import { ref, computed } from "vue";
import { useLoginStore } from "@/modules/login/store/login_store";
import { loginService } from "@/shared/services/login_service";
import { useRoute, useRouter } from "vue-router";
import { useDetalhesProposta } from "@/modules/detalhes_proposta/store/detalhes_proposta_store";

const menu = ref(false);
const loginStore = useLoginStore();
const detalhesPropostaStore = useDetalhesProposta();
const route = useRoute();
const router = useRouter();

function logout() {
  loginService.logout();
}

const pageTitle = computed(() => {
  if (route.name === "DetalhesProposta") {
    return detalhesPropostaStore.currentStepName;
  }

  if (route.name === "Financiamentos") {
    return "Portal do Integrador";
  }

  return (route.meta?.title as string) ?? "";
});

const isActiveRoute = (routeName: string) => {
  return route.name === routeName;
};

const navigateTo = (routeName: string) => {
  router.push({ name: routeName });
};

// Menus baseados no perfil
const menuItems = computed(() => {
  const profileSlug = loginStore.authUser?.data?.profileSlug ?? "";

  switch (profileSlug) {
    case "Integrador":
    case "Adm-Integrador":
      return [
        { label: "Financiamentos", routeName: "Financiamentos" },
        { label: "Meus Dados", routeName: "MeusDados" },
      ];

    case "BackOffice-Engenharia":
      return [{ label: "Análises", routeName: "BackofficeEngenharia" }];

    case "BackOffice-Comercial":
      return [
        { label: "Integradores", routeName: "BackofficeComercial" },
        {
          label: "Financiamentos Reprovados",
          routeName: "FinanciamentosReprovados",
        },
      ];

    case "BackOffice-Financeiro":
      return [{ label: "Notas Fiscais", routeName: "BackofficeFinanceiro" }];

    case "Administrador":
      // Admin tem acesso a todos os menus
      return [
        { label: "Financiamentos", routeName: "Financiamentos" },
        { label: "Meus Dados", routeName: "MeusDados" },
        // { label: "Backoffice Engenharia", routeName: "BackofficeEngenharia" },
        // { label: "Backoffice Comercial", routeName: "BackofficeComercial" },
        // { label: "Backoffice Financeiro", routeName: "BackofficeFinanceiro" },
      ];

    default:
      return [];
  }
});
</script>
<template>
  <v-app-bar flat height="72" color="white" class="px-6 navbar">
    <v-row no-gutters align="center" class="w-100">
      <!-- Logo -->
      <v-col cols="auto" class="d-flex align-center">
        <img
          src="@/assets/LOGO-Liberta-Financiamentos.svg"
          alt="Liberta Financiamentos"
          class="logo"
        />
      </v-col>
      <!-- Menu -->
      <v-col cols="auto" class="d-flex align-center ga-6 menu-links">
        <span
          v-for="item in menuItems"
          :key="item.routeName"
          class="menu-item"
          :class="{ active: isActiveRoute(item.routeName) }"
          @click="navigateTo(item.routeName)"
        >
          {{ item.label }}
        </span>
      </v-col>
      <!-- Título central -->
      <v-col class="d-flex justify-center" cols>
        <span class="page-title">{{ pageTitle }}</span>
      </v-col>
      <!-- Usuário -->
      <v-col cols="auto" class="d-flex justify-end">
        <v-menu
          v-model="menu"
          transition="fade-transition"
          location="bottom end"
          :close-on-content-click="true"
        >
          <template #activator="{ props }">
            <div class="d-flex align-center ga-3 user-activator" v-bind="props">
              <v-icon size="28" color="orange-darken-3">
                mdi-account-circle-outline
              </v-icon>
              <div class="d-flex flex-column text-right">
                <span class="welcome">Boas-vindas</span>
                <span class="user-name">
                  {{ loginStore.usuarioLogado?.nome ?? "" }}
                </span>
              </div>
              <v-icon size="18">mdi-chevron-down</v-icon>
            </div>
          </template>
          <v-sheet elevation="6" class="menu-sheet">
            <v-list density="compact">
              <v-list-item>
                <v-list-item-title>Usuários</v-list-item-title>
              </v-list-item>
              <v-divider />
              <v-list-item>
                <v-list-item-title>Perfis</v-list-item-title>
              </v-list-item>
              <v-divider />
              <v-list-item @click="logout" class="sair-item">
                <v-list-item-title>Sair</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-sheet>
        </v-menu>
      </v-col>
    </v-row>
  </v-app-bar>
</template>
<style scoped>
.navbar {
  border-bottom: 1px solid #e0e0e0;
  height: 96px;
  display: flex;
  justify-content: center;
}
.logo {
  width: 111px;
}
.menu-links {
  margin-left: 32px;
}
.menu-item {
  font-size: 16px;
  color: #2c2c2c;
  cursor: pointer;
  position: relative;
  padding-right: 16px;
  border-right: 1px solid #cfcfcf;
  font-family: "Qubic Grid", sans-serif;
}
.menu-item.active {
  font-weight: 600;
}
.menu-item.active::after {
  content: "";
  position: absolute;
  bottom: -6px;
  left: 2px;
  right: 16px;
  height: 2px;
  background-color: #f57c00;
  border-radius: 2px;
}
.page-title {
  font-size: 24px;
  font-weight: 500;
  color: #2c2c2c;
  font-family: "DM Sans", sans-serif;
}
.user-activator {
  cursor: pointer;
}
.welcome {
  font-size: 12px;
  color: #7a7a7a;
}
.user-name {
  font-size: 14px;
  font-weight: 600;
  color: #2c2c2c;
}
.menu-sheet {
  width: 165px;
  border-radius: 6px;
  background: white;
}
.v-list-item {
  height: 48px;
  align-items: center;
  padding-left: 16px;
  cursor: pointer;
}
.v-divider {
  margin: 0;
}
.sair-item .v-list-item-title {
  color: #f57c00;
  font-weight: 600;
}
</style>
